import { forwardRef, InputHTMLAttributes, LegacyRef } from 'react';
import { cn } from '@/utils/classNames.ts';
import { InputProps } from 'react-daisyui';
import dayjs, { Dayjs } from '$/utils/dayjs';

type DateTimeInputProps = { onChange?: (value: string | undefined) => void } & Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'size' | 'color' | 'onChange' | 'value'
> &
  Pick<InputProps, 'size' | 'color' | 'bordered'> & {
    label?: string;
    error?: string;
    wrapperClassName?: string;
  } & { value: string; displayFormat?: string; timeZone?: string; min?: string; max?: string };

const DateTimeInput = forwardRef(
  (
    {
      label,
      error,
      wrapperClassName,
      size = 'md',
      color,
      bordered = true,
      value,
      onChange,
      className,
      min,
      max,
      ...args
    }: DateTimeInputProps,
    ref: LegacyRef<HTMLInputElement>
  ) => {
    const handleSelect = (value: Dayjs | undefined) => {
      if (onChange) {
        onChange(value ? value.toISOString() : value);
      }
    };

    return (
      <div className={cn('form-control max-w-full w-full group', wrapperClassName)}>
        {label ? (
          <label className="label">
            <span className="label-text">{label}</span>
          </label>
        ) : null}
        <label
          className={cn('input flex items-center justify-between gap-2 dropdown cursor-pointer max-w-full', {
            'input-ghost': color === 'ghost',
            'input-primary': color === 'primary',
            'input-secondary': color === 'secondary',
            'input-accent': color === 'accent',
            'input-info': color === 'info',
            'input-success': color === 'success',
            'input-warning': color === 'warning',
            'input-error': error || color === 'error',
            'input-xs': size === 'xs',
            'input-sm': size === 'sm',
            'input-md': size === 'md' || size,
            'input-lg': size === 'lg',
            'input-bordered': bordered,
          })}
        >
          <input
            tabIndex={0}
            type="datetime-local"
            className={cn('text-base grow cursor-pointer overflow-auto', className)}
            min={min}
            max={max}
            ref={ref}
            value={value ? dayjs.utc(value).local().format('YYYY-MM-DDTHH:mm') : ''}
            onChange={(e) => handleSelect(e.target.value ? dayjs(e.target.value).utc() : undefined)}
            {...args}
          />
        </label>
        {error ? (
          <label className="label">
            <span className="label-text-alt text-error">{error}</span>
          </label>
        ) : null}
      </div>
    );
  }
);

export default DateTimeInput;
